/**
 * Unified ContentService — central environment-driven configuration.
 *
 * Replaces the scattered per-feature env switches
 * (REACT_APP_LOCAL_DISK, REACT_APP_GALLERY_REDUCE, REACT_APP_FSI_LOCAL_DEV_*,
 * REACT_APP_HOSPITALITY_MENU_FROM_BACKEND, REACT_APP_BACKEND_UPSTREAM, …) with one
 * policy object consulted by every adapter. See docs/content-service-design.md §2.3.
 */

function env(name, fallback = '') {
  const v = process.env[name];
  return v === undefined || v === null ? fallback : String(v);
}

function boolEnv(name, def) {
  const v = env(name);
  if (v === '1' || v === 'true' || v === 'yes') return true;
  if (v === '0' || v === 'false' || v === 'no') return false;
  return def;
}

function isLocalDiskDev() {
  if (env('REACT_APP_LOCAL_DISK') === '1') return true;
  if (env('REACT_APP_LOCAL_DISK') === '0') return false;
  if (env('REACT_APP_FSI_ALL_USE_DISK') === '1') return true;
  if (env('REACT_APP_FSI_FRENCH_USE_DISK') === '1') return true;
  if (env('REACT_APP_FSI_RUSSIAN_USE_DISK') === '1') return true;
  return false;
}

/**
 * @returns {{
 *   isDev: boolean,
 *   isProd: boolean,
 *   localDisk: boolean,
 *   localDiskRoot: string,
 *   publicUrl: string,
 *   backendUpstream: string,
 *   cloudfrontMediaBase: string,
 *   hospitalityMenuCdnBase: string,
 * }}
 */
export function getContentConfig() {
  const isProd = env('NODE_ENV') === 'production';
  return {
    isDev: !isProd,
    isProd,
    localDisk: isLocalDiskDev(),
    localDiskRoot: env('MGV_LOCAL_GALLERY_ROOT', 'I:/mgv-assets'),
    publicUrl: env('PUBLIC_URL', ''),
    // Where the dev proxy / backend lives. When REACT_APP_BACKEND_UPSTREAM is set
    // the dev proxy hits the ECS box directly (bypassing the CloudFront /api 502).
    backendUpstream:
      env('REACT_APP_BACKEND_UPSTREAM', isProd ? '' : 'http://localhost:3000'),
    cloudfrontMediaBase: env('REACT_APP_CLOUDFRONT_MEDIA_BASE', '').replace(/\/+$/, ''),
    hospitalityMenuCdnBase: env('REACT_APP_HOSPITALITY_MENU_CDN_BASE', '').replace(/\/+$/, ''),
  };
}

/**
 * Build a same-origin URL from a path. Static/media asset URLs are always
 * same-origin (relative or PUBLIC_URL-prefixed); they must NOT be pointed at
 * `backendUpstream` (that is only for /api proxying, handled in setupProxy).
 */
export function sameOriginUrl(path, { publicUrl = '' } = {}) {
  const p = String(path).replace(/^\/+/, '');
  const base = (publicUrl || '').replace(/\/+$/, '');
  return base ? `${base}/${p}` : `/${p}`;
}
